import { useAutoRefresh } from '../../hooks/use-auto-refresh';
import { getRecentScans } from '../../api/stats';
import { EntityLink } from '../../components/entity-link';
import { Spinner } from '../../components/spinner';
import { EmptyState } from '../../components/empty-state';
import type { RecentScan } from '../../api/types';

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function ScanIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <line x1="7" y1="12" x2="17" y2="12" />
    </svg>
  );
}

function FeedItem({ scan }: { scan: RecentScan }) {
  return (
    <div class="enhanced-feed-item">
      <div class="enhanced-feed-icon scan">
        <ScanIcon />
      </div>
      <div class="enhanced-feed-content">
        <div class="enhanced-feed-text">
          <EntityLink
            type="product"
            id={scan.barcode}
            label={scan.product_name ?? scan.barcode}
          />
          {' scanned'}
          {scan.product_name && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-tertiary)', marginLeft: '4px' }}>
              {scan.barcode}
            </span>
          )}
        </div>
        <div class="enhanced-feed-time">{timeAgo(scan.scanned_at)}</div>
      </div>
    </div>
  );
}

export function EnhancedActivityFeed() {
  const { data, loading, error } = useAutoRefresh(getRecentScans, 30_000);

  if (loading && !data) {
    return <Spinner text="Loading activity" />;
  }

  if (error) {
    return <EmptyState icon="error" message="Failed to load activity" hint={error} />;
  }

  const scans = data?.entries ?? [];

  if (scans.length === 0) {
    return <EmptyState icon="search" message="No recent activity" />;
  }

  return (
    <div>
      <div class="enhanced-feed">
        {scans.map((scan, i) => (
          <FeedItem scan={scan} key={`${scan.barcode}-${i}`} />
        ))}
      </div>
      <div class="enhanced-feed-refresh">
        <span class="dot" />
        <span>Auto-refreshing every 30s</span>
      </div>
    </div>
  );
}
